import { useState } from 'react'
import { Modal, colors } from '../../components/ui'
import { ConfigComparisonView } from './ConfigComparisonView'
import { DeviceActivityTab } from './DeviceActivityTab'
import { relativeTime } from './fleet-helpers'

export interface DeviceSnapshot {
  readonly id: string
  readonly organizationId: string
  readonly organizationName: string | null
  readonly tenancyId: string | null
  readonly siteId: string | null
  readonly siteName: string | null
  readonly deviceId: number
  readonly instanceSeq: number
  readonly deviceName: string
  readonly deviceType: string
  readonly protocol: string
  readonly status: string
  readonly currentConfigVersion: number | null
  readonly udmVersion: string | null
  readonly startedAt: string | null
  readonly lastResultAt: string | null
  readonly errorCountLast24h: number
  readonly lastHeartbeatAt: string
  readonly source: string
  readonly configDriftDetectedAt: string | null
  readonly restartState: string | null
  readonly pendingRestartConfigVersion: number | null
}

interface Props {
  readonly device: DeviceSnapshot
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly isMedflowAdmin: boolean
}

type TabKey = 'status' | 'config' | 'activity'

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '0.5rem 1rem',
  border: 'none',
  borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
  background: 'transparent',
  color: active ? colors.accent : colors.textMuted,
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
  fontSize: '13px',
})

const fieldRow: React.CSSProperties = {
  display: 'flex',
  padding: '0.5rem 0',
  borderBottom: `1px solid ${colors.border}`,
  fontSize: '13px',
}

const fieldLabel: React.CSSProperties = {
  width: '140px',
  fontWeight: 500,
  color: colors.textMuted,
  flexShrink: 0,
}

export function DeviceDetailModal({ device, isOpen, onClose, isMedflowAdmin }: Props) {
  const [tab, setTab] = useState<TabKey>('status')

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={device.deviceName} width="wide">
      <div style={{ display: 'flex', gap: '0', borderBottom: `1px solid ${colors.border}`, marginBottom: '1rem' }}>
        <button type="button" onClick={() => setTab('status')} style={tabStyle(tab === 'status')}>
          Status
        </button>
        <button type="button" onClick={() => setTab('activity')} style={tabStyle(tab === 'activity')}>
          Activity
        </button>
        {isMedflowAdmin && (
          <button type="button" onClick={() => setTab('config')} style={tabStyle(tab === 'config')}>
            Config
            {device.configDriftDetectedAt && (
              <span
                style={{
                  marginLeft: '6px',
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                }}
              />
            )}
            {device.restartState === 'pending' && (
              <span
                title={
                  device.pendingRestartConfigVersion != null
                    ? `Config v${device.pendingRestartConfigVersion}`
                    : undefined
                }
                style={{
                  marginLeft: '6px',
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  background: '#ede9fe',
                  color: '#6d28d9',
                  fontSize: '10px',
                  fontWeight: 600,
                }}
              >
                Restart pending
              </span>
            )}
          </button>
        )}
      </div>

      {tab === 'status' && (
        <div>
          {(
            [
              ['Device ID', `${device.deviceId}.${device.instanceSeq}`],
              ['Type', device.deviceType],
              ['Protocol', device.protocol || '—'],
              ['Status', device.status],
              ['UDM Version', device.udmVersion ?? '—'],
              ['Config Version', device.currentConfigVersion ?? '—'],
              ['Last Heartbeat', relativeTime(device.lastHeartbeatAt)],
              ['Errors (24h)', device.errorCountLast24h],
              ['Site', device.siteName ?? device.siteId ?? '—'],
              ['Source', device.source],
            ] as const
          ).map(([label, value]) => (
            <div key={String(label)} style={fieldRow}>
              <span style={fieldLabel}>{String(label)}</span>
              <span
                style={{
                  color: label === 'Errors (24h)' && Number(value) > 0 ? colors.danger : 'inherit',
                }}
              >
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {tab === 'activity' && <DeviceActivityTab device={device} />}

      {tab === 'config' && isMedflowAdmin && <ConfigComparisonView device={device} />}
    </Modal>
  )
}
